<template>
  <div>
    <v-skeleton-loader
      type="table-thead, table-tbody"
      width="100%"
      :loading="openOrderTableLoader"
      class="pa-4"
      boilerplate
    >
      <v-container class="prices-list-table pa-0 fill-height" fluid>
        <v-card width="100%" class="fill-height" color="gray10 px-5 py-2">
          <v-row class="fill-height">
            <v-col cols="12">
              <v-row no-gutters justify="space-between" align="center">
                <v-col class="py-0">
                  <p class="mb-0 font-Medium-14 gray1--text">
                    {{ $t("openOrders.openOrders") }}
                  </p>
                </v-col>
                <v-col cols="auto">
                  <v-btn
                    color="primary"
                    class="white--text float-left"
                    @click="drawer = true"
                  >
                    {{ $t("openOrders.filter") }}
                    <v-icon right dark>
                      mdi-filter
                    </v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12">
              <!--Table-->
              <OpenOrdersTable
                :items="orders"
                :pageNo="pageNo"
                :pageSize="pageSize"
                @cancelCreatedOrder="cancelCreatedOrder"
              />
              <v-col class="py-0 my-3" cols="12">
                <v-row justify="center">
                  <v-col class="py-0 text-center" cols="12">
                    <v-pagination
                      class="custom-number-pagination"
                      v-model="pageNo"
                      total-visible="6"
                      :length="totalPages"
                      @input="getCurrentOrders"
                    ></v-pagination>
                  </v-col>
                </v-row>
              </v-col>
            </v-col>
          </v-row>
        </v-card>
      </v-container>
    </v-skeleton-loader>
    <v-navigation-drawer
      v-model="drawer"
      absolute
      temporary
      width="500px"
      class="pa-5 gray9"
    >
      <date-picker
        v-model="transactionTime"
        format="YYYY-DD-MM"
        :display-format="!userInfo.is_global ? 'jYYYY-jMM-jDD' : 'YYYY-DD-MM'"
        :locale="userInfo.is_global ? 'en' : 'fa'"
        :popover="$vuetify.rtl ? 'bottom-right' : 'bottom-left'"
        color="#500fe9"
        clearable
        class="custom-date-picker mb-4"
        range
      />
      <v-autocomplete
        v-model="formItem.symbol"
        :items="symbol"
        item-text="symbol"
        filled
        :placeholder="$t('openOrders.pairCoin')"
        :menu-props="{ bottom: true, offsetY: true }"
        class="mt-8"
        outlined
        :no-data-text="$t('common.nodata')"
      ></v-autocomplete>
      <v-select
        :items="orderTypes"
        :placeholder="$t('openOrders.orderType')"
        outlined
        item-text="text"
        :menu-props="{ bottom: true, offsetY: true }"
        v-model="formItem.source"
      >
      </v-select>
      <v-select
        :items="directions"
        :placeholder="$t('openOrders.direction')"
        item-text="title"
        outlined
        :menu-props="{ bottom: true, offsetY: true }"
        v-model="formItem.direction"
      ></v-select>
      <v-row>
        <v-col>
          <v-btn block text color="primary" @click="getCurrentOrders">
            {{ $t("openOrders.search") }}
          </v-btn>
        </v-col>
        <v-col>
          <v-btn block color="error" text @click="clearFilter">
            {{ $t("openOrders.clearFilters") }}
          </v-btn>
        </v-col>
      </v-row>
    </v-navigation-drawer>
  </div>
</template>

<script>
import OpenOrdersTable from "@/components/Market/openOrders/OpenOrdersTable.vue";
import VuePersianDatetimePicker from "vue-persian-datetime-picker";
import { ExchangeService, MarketMarketService } from "@/services";
import dayjs from "dayjs";
import * as types from "@/store/types";
import { mapGetters } from "vuex";
export default {
  components: { datePicker: VuePersianDatetimePicker, OpenOrdersTable },
  data() {
    return {
      drawer: false,
      transactionTime: "",
      symbol: [],
      orderTypes: [
        {
          value: "limit",
          text: this.$t("openOrders.limit")
        },
        {
          value: "market",
          text: this.$t("openOrders.market")
        },
        {
          value: "stop_limit",
          text: this.$t("openOrders.stopLimit")
        },
        {
          value: "oco",
          text: this.$t("openOrders.oco")
        }
      ],
      sourceMapping: {
        limit: { type: "LIMIT_PRICE", source: "MANUAL_ORDER" },
        stop_limit: { type: "LIMIT_PRICE", source: "STOP_LIMIT_ORDER" },
        market: { type: "MARKET_PRICE", source: "MANUAL_ORDER" },
        oco: { type: "LIMIT_PRICE", source: "OCO_ORDER" }
      },
      directions: [
        { title: this.$t("openOrders.buy"), value: 0 },
        { title: this.$t("openOrders.sell"), value: 1 }
      ],
      pageSize: 10,
      totalPages: 0,
      pageNo: 1,
      total: 10,
      openOrderTableLoader: false,
      formItem: {
        symbol: "",
        type: "",
        source: "",
        direction: "",
        date: "",
        orderType: ""
      },
      loading: false,
      orders: []
    };
  },
  computed: {
    ...mapGetters({
      userInfo: types.userInfo.getters.GET_USER_INFO
    })
  },
  mounted() {
    this.formItem.date = new Date().getTime();
    this.getCurrentOrders();
    this.getSymbol();
  },
  methods: {
    setSource() {
      const mapping = this.sourceMapping[this.formItem.source];
      if (!mapping) {
        return { type: "", source: "" };
      }
      const type = mapping.type || "";
      const source = mapping.source || "";
      return { type, source };
    },
    getCurrentOrders() {
      this.loading = true;
      const { type, source } = this.setSource();
      const { symbol, direction } = this.formItem;
      let startTime, endTime;
      if (this.transactionTime) {
        startTime = dayjs(this.transactionTime[0], "YYYY-DD-MM").valueOf();
        endTime = this.transactionTime[1]
          ? dayjs(this.transactionTime[1], "YYYY-DD-MM").valueOf()
          : null;
      }
      let payload = {
        symbol: symbol,
        direction: direction,
        type: type,
        source: source,
        startTime: startTime,
        endTime: endTime,
        pageNo: this.pageNo,
        pageSize: this.pageSize
      };
      Object.keys(payload).forEach(key => {
        if (payload[key] === "" || payload[key] === undefined) {
          delete payload[key];
        }
      });
      this.orders = [];
      ExchangeService.geOrdersCurrentList(payload)
        .then(response => {
          let ordersCurrentList = response.data.content;
          this.total = response.data.totalElements;
          this.totalPages = response.data.totalPages;
          let rows = [];
          if (ordersCurrentList.length > 0) {
            for (let i = 0; i < ordersCurrentList.length; i++) {
              let row = ordersCurrentList[i];
              row.price =
                row.type === "MARKET_PRICE"
                  ? this.$t("exchange.marketprice")
                  : row.price;
              rows.push(row);
            }
            this.orders = rows;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getSymbol() {
      MarketMarketService.getSymbolsThumb().then(response => {
        if (response.data.length > 0) {
          this.symbol = response.data;
        }
      });
    },
    cancelCreatedOrder(orderId) {
      ExchangeService.orderCancel(orderId)
        .then(() => {
          this.getCurrentOrders();
          this.$toast.success(this.$t("openOrders.successCancel"));
        })
        .catch(err => {
          this.$toast.error(err.message);
        });
    },
    clearFilter() {
      this.transactionTime = "";
      this.formItem = {
        symbol: "",
        type: "",
        source: "",
        direction: "",
        date: "",
        orderType: ""
      };
      this.getCurrentOrders();
    }
  }
};
</script>

<style lang="scss">
.custom-date-picker {
  width: 100%;
  .vpd-input-group input {
    width: 100% !important;
    color: var(--v-onPrimary-base);
    border: 1px solid var(--v-gray3-base);
  }
}
</style>
